'use client';

import { useMemo, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import Button from '../../atoms/button';
import Text from '../../atoms/text';
import Icon from '../../atoms/icon';
import styles from './alert.module.css';
import { ALERT_ANIMATIONS, ALERT_TYPES, ALERT_VARIANTS, ALERT_POSITIONS } from './alert.enum';
import { ALERT_DEFAULT_ANIMATION_DURATION, ALERT_ICON_MAP } from './alert.constants';
import { animationClassMap } from './alert.animations';
import { useAlertVisibility } from './use-alert-visibility';
import type { AlertProps } from './alert.types';

export function Alert({
    type = ALERT_TYPES.INFO,
    title,
    titleProps,
    message,
    messageProps,
    onDismiss,
    showIcon = true,
    className,
    position,
    duration,
    animationDuration = ALERT_DEFAULT_ANIMATION_DURATION,
    useAsPortal = true,
    animation = ALERT_ANIMATIONS.FADE,
    buttonProps,
    variant = ALERT_VARIANTS.INLINE,
    autoClose = false,
}: AlertProps) {
    const iconName = ALERT_ICON_MAP[type];

    const alertPosition = position ?? ALERT_POSITIONS.TOP_RIGHT;

    const { isMounted, isVisible, handleDismiss } = useAlertVisibility({
        autoClose,
        duration,
        animationDuration,
        onDismiss,
    });

    const shouldPortal = variant === ALERT_VARIANTS.FLOATING && useAsPortal;

    const rootClassName = useMemo(
        () => cn(styles.root, styles[variant], variant === ALERT_VARIANTS.FLOATING && styles[alertPosition]),
        [variant, alertPosition]
    );

    const alertClassName = useMemo(
        () =>
            cn(
                styles.alert,
                styles[type],
                animationClassMap[animation],
                isVisible ? styles.enter : styles.exit,
                className
            ),
        [type, animation, isVisible, className]
    );

    const titleClassName = useMemo(() => cn(styles.title, titleProps?.className), [titleProps?.className]);

    const messageClassName = useMemo(() => cn(styles.message, messageProps?.className), [messageProps?.className]);

    const dismissButtonClassName = useMemo(
        () => cn(styles.dismissButton, buttonProps?.className),
        [buttonProps?.className]
    );

    const alertStyle = useMemo(
        () =>
            ({
                ['--alert-animation-duration' as string]: `${animationDuration}ms`,
            }) as CSSProperties,
        [animationDuration]
    );

    const buttonContent = buttonProps?.children ?? (
        <Icon name="x" size="medium" className={styles.dismissIcon} aria-hidden={true} />
    );

    if (!isMounted && shouldPortal) return null;

    const alertUI = (
        <div className={rootClassName}>
            <div className={alertClassName} role="alert" style={alertStyle}>
                {showIcon && iconName ? (
                    <Icon name={iconName} size="medium" className={styles.icon} aria-hidden={true} />
                ) : null}

                <div className={styles.body}>
                    <Text showIf={!!title} className={titleClassName} {...titleProps}>
                        {title}
                    </Text>

                    <Text showIf={!!message} className={messageClassName} {...messageProps}>
                        {message}
                    </Text>
                </div>

                <Button
                    showIf={!!onDismiss}
                    {...buttonProps}
                    onClick={handleDismiss}
                    className={dismissButtonClassName}
                    aria-label={buttonProps?.['aria-label'] ?? 'Dismiss alert'}
                >
                    {buttonContent}
                </Button>
            </div>
        </div>
    );

    if (!shouldPortal) return alertUI;

    return createPortal(alertUI, document.body);
}
